import { useCurrentCanvasValue } from "frontend/canvas-designer-new/canvas-context";
import { ITraits, Trait } from "frontend/canvas-designer-new/elements-toolbar/elements-toolbar-types";
import {
  filterOutEmptyValues,
  lookupBoardId,
  useBoardFullInfo,
  useBoardIntegrations,
  useBoardTasks,
} from "frontend/hooks/use-board-integrations";
import { clamp, isPointInRect, viewportToStage } from "frontend/utils/math-utils";
import { useAtomValue } from "jotai";
import Konva from "konva";
import { equals, pluck } from "rambda";
import { useEffect, useMemo, useState } from "react";
import { Circle, Ellipse, Group, Line, Rect, Text } from "react-konva";
import {
  CARD_HEIGHT,
  DEFAULT_CARD_STACK_HEIGHT,
  DEFAULT_CARD_STACK_WIDTH,
  GAP_Y,
  HEADER,
  PADDING_X,
  PADDING_Y_BOTTOM_WITH_MESSAGE,
  PADDING_Y_TOP,
} from "shared/datamodel/card-stack";
import { Point } from "shared/datamodel/schemas";
import { LiveIntegrationElement } from "shared/datamodel/schemas/live-integration";
import { IntegrationType, MondayIntegrationType } from "shared/integrations/integration";
import { utilsAtom } from "state-atoms";
import BaseCanvasElement from "../base-canvas-element";
import { TransformHooksCardStack } from "./card-stack-element";
import { CardStackFooter } from "./card-stack-subelements";
import {
  cardGridHeightParams,
  cardGridHeightParamsWithUndisplayedCards,
  cardGridWidthParams,
  cardPosition,
  computeColumnsAndRows,
  sizeOfCardsLine,
} from "./card-stack-utils";
import { BoardInfo, LiveCardStackContents } from "./live-card-stack-contents";
import { LiveCardStackFrame } from "./live-card-stack-frame";
import tracking from "frontend/tracking";
import { useMondayItemManager } from "frontend/canvas-designer-new/integrations/components/monday-item-manager-context";
import { useMondayLiveList } from "frontend/canvas-designer-new/integrations/hooks/use-monday-live-list";
import { useDeepEqualMemo } from "frontend/hooks/use-deep-memoize";

export default function LiveIntegrationCardStack({
  id,
  element,
  changeElement,
  onChangeElement,
  isEditing,
  onStopEditing,
  onResize,
}: {
  id: string;
  element: LiveIntegrationElement;
  changeElement: any;
  onChangeElement: (
    id: string,
    props: Map<string, any>,
    previousProps: Map<string, any>,
    addUndo: boolean,
    updateSubMenuData?: any
  ) => void;
  isEditing: boolean;
  onStopEditing: () => void;
  onResize: (id: string, position: Point, scaleX: number, scaleY: number, rotation: number) => void;
}) {
  const mutation = useMemo(() => new TransformHooksCardStack(id, onResize), [id, onResize]);
  const [{ documentId }] = useCurrentCanvasValue();
  const canvasUtils = useAtomValue(utilsAtom(documentId));
  const {
    mondayIntegration,
    addMondayIntegration,
    addTemporaryBoardIntegration,
    tasksIntegrations,
    getBoardIntegrationConfig,
    updateDataSource,
  } = useBoardTasks(documentId);

  const { filters, integrationId, items_order = [], showSubItems, subitems_order = {} } = element;
  const [subItemsIntegrationId, setSubItemsIntegrationId] = useState<string | undefined>();

  const { boardIntegrations } = useBoardIntegrations(documentId);
  const boardId = lookupBoardId(integrationId, boardIntegrations);
  const boardInfo = useBoardFullInfo(documentId, integrationId, boardId);
  const isError = integrationId && !boardId && !boardInfo.data;

  let selectedBoardId: string | undefined,
    selectedSubBoardId: string | undefined = undefined;
  let boardColumns: any[] = [],
    subitemsColumns: any[] = [];

  if (!boardInfo.error && boardInfo.data) {
    selectedBoardId = boardInfo.data?.board?.id;
    boardColumns = boardInfo.data?.board?.columns?.filter(
      ({ type }: { type: string }) => type !== "subtasks" && !type.startsWith("subitem")
    );
    subitemsColumns = boardInfo.data.subitemsBoard?.columns;
    selectedSubBoardId = boardInfo.data.subitemsBoard?.id;
  }

  useEffect(() => {
    // update integration columns+board-name with recent data
    if (integrationId && boardInfo.data?.board) {
      const intg = getBoardIntegrationConfig(integrationId);
      if (intg) {
        const boardName = boardInfo.data.board.name,
          columns = boardInfo.data.board.columns;
        if (boardName != intg.boardName || !equals(columns, intg.columns)) {
          updateDataSource(integrationId, { name: boardName, columns });
        }
      }
    }
  }, [integrationId, boardInfo.data]);

  useEffect(() => {
    // add integration to the subitems board. also update columns
    if (selectedSubBoardId) {
      const columns = subitemsColumns;
      const defaultColumns = columns.filter((col) => col.type === "status" || col.type === "date").slice(0, 2);

      // decide what kind of integration we're adding here
      let addIntegration = mondayIntegration
        ? addMondayIntegration(
            selectedSubBoardId,
            mondayIntegration.id,
            subitemsColumns,
            MondayIntegrationType.live,
            defaultColumns, //selected columns
            selectedBoardId, //parent board
            true //subitems-board
          )
        : Promise.resolve(() =>
            addTemporaryBoardIntegration(selectedSubBoardId!, {
              boardName: boardInfo.data.subitemsBoard.name,
              config: { columns: defaultColumns },
              columns: [...columns],
            })
          );
      // and then save the id
      addIntegration.then(setSubItemsIntegrationId).catch(console.error);
    }
  }, [selectedSubBoardId, mondayIntegration]);

  // update our dataSource global with info about the sub-items board
  useEffect(() => {
    if (subItemsIntegrationId) {
      const intg = getBoardIntegrationConfig(subItemsIntegrationId);
      if (intg && boardInfo?.data?.subitemsBoard) {
        const subboardName = boardInfo.data.subitemsBoard.name,
          subcolumns = boardInfo.data.subitemsBoard.columns;
        if (subboardName != intg.boardName || !equals(subcolumns, intg.columns)) {
          updateDataSource(subItemsIntegrationId, { boardName: subboardName, columns: subcolumns });
        }
      }
    }
  }, [subItemsIntegrationId]);

  const ctx = useMondayItemManager();

  const validFilters = useDeepEqualMemo(filterOutEmptyValues(filters ?? []));

  const data = useMondayLiveList(integrationId, boardInfo?.data?.board, validFilters);

  const itemIds: string[] = data.data ?? [];
  const isLoading = data.isLoading;
  const isValidating = isLoading;

  let allItems = itemIds
    ? itemIds.reduce((acc, cur) => {
        const data = ctx.getSingleItem(cur);
        if (data?.data) acc.push(data.data);
        return acc;
      }, [] as any[])
    : [];
  const hasMore = data?.hasMore;

  // Order the items like this:
  // everything not in items_order, comes first, in the same way they are ordered in allItems
  // things in item_order, comes second, in the order they are in items_order
  if (items_order.length > 0) {
    const positions = allItems.reduce((acc: any, item: any, index: number) => {
      if (!item) return acc;
      const itemId = item.id;
      const order = items_order.indexOf(itemId);
      if (order === -1) {
        acc[itemId] = index;
      } else {
        // this will start positioning items from the end of the items_order
        // making sure items that are not in the items_order will be positioned first
        acc[itemId] = allItems.length + order;
      }
      return acc;
    }, {});
    allItems.sort((a, b) => positions[a.id] - positions[b.id]);
  }

  // Apply subitem ordering for each parent item
  allItems = allItems.map((item) => {
    if (item.subitems && item.subitems.length > 0) {
      const parentSubitemsOrder = subitems_order[item.id] || [];
      const subitemPositions = item.subitems.reduce((acc: any, subitem: any, index: number) => {
        const subitemId = subitem.id;
        const order = parentSubitemsOrder.indexOf(subitemId);
        if (order === -1) {
          acc[subitemId] = index;
        } else {
          acc[subitemId] = item.subitems.length + order;
        }
        return acc;
      }, {});
      const sortedSubitems = [...item.subitems].sort((a, b) => subitemPositions[a.id] - subitemPositions[b.id]);
      return { ...item, subitems: sortedSubitems };
    }
    return item;
  });

  const mainBoardIntegrationConfig: any = integrationId ? getBoardIntegrationConfig(integrationId) : {};
  const columnIdsInMainBoard = new Set(
    pluck("id", (mainBoardIntegrationConfig?.config?.columns || []) as { id: string }[])
  );

  const cardLayoutStartY = element.y + HEADER + PADDING_Y_TOP;

  const { columns, rows } = computeColumnsAndRows(
    element.width ?? DEFAULT_CARD_STACK_WIDTH,
    element.height ?? DEFAULT_CARD_STACK_HEIGHT,
    element.scaleX,
    element.scaleY
  );

  const numItems =
    allItems.length + (showSubItems ? allItems.reduce((acc, cur) => acc + (cur.subitems_ids?.length ?? 0), 0) : 0);
  const numCardsToDisplay = Math.min(numItems, columns * rows);
  const numOfUndisplayedCards = numItems - numCardsToDisplay;

  const width = sizeOfCardsLine(columns, cardGridWidthParams);
  const heightFromElement = (element.height ?? DEFAULT_CARD_STACK_HEIGHT) * (element.scaleY ?? 1);
  const heightForCards = sizeOfCardsLine(
    rows,
    numOfUndisplayedCards === 0 ? cardGridHeightParams : cardGridHeightParamsWithUndisplayedCards
  );

  useEffect(() => {
    // shrink the stack if it's too tall
    if (isLoading) {
      return;
    }
    const requiredHeight = sizeOfCardsLine(
      Math.max(numItems, 2),
      numOfUndisplayedCards === 0 ? cardGridHeightParams : cardGridHeightParamsWithUndisplayedCards
    );
    if (requiredHeight < heightForCards) {
      onChangeElement(
        id,
        new Map([["scaleY", requiredHeight / (element.height ?? DEFAULT_CARD_STACK_HEIGHT)]]),
        new Map([["scaleY", element.scaleY ?? 1]]),
        true
      );
    }
  }, [validFilters, integrationId, isLoading]);

  function expandHeightForAllCards() {
    const newHeight = sizeOfCardsLine(Math.max(numItems, 2), cardGridHeightParams);
    const { scaleY = 1 } = element;
    const newScale = newHeight / (element.height ?? DEFAULT_CARD_STACK_HEIGHT);
    const props = new Map([["scaleY", newScale]]);
    const prevProps = new Map([["scaleY", scaleY]]);
    onChangeElement(id, props, prevProps, true);
  }

  function onDragEnd(e: any, b: BoardInfo, selectedColumns: any[], itemId: string) {
    try {
      if (!e.evt) {
        // if evt doesn't exist, that means konva stopped the drag operation not because
        // the user stopped, but because the mouse left the canvas (maybe it's over html).
        // This is not an error or exception, so no panic.
        return;
      }
      const stage: Konva.Stage = e.target.getStage();
      const stageParams = { x: stage.x(), y: stage.y(), scale: stage.scaleX() };
      const mousePosition = viewportToStage(stageParams, { x: e.evt.offsetX, y: e.evt.offsetY });
      const cardStackArea = {
        x: element.x,
        y: element.y,
        width,
        height: heightFromElement,
      };
      const isSubitemsBoard = b.boardId === selectedSubBoardId;
      const parentBoardId = isSubitemsBoard ? selectedBoardId : undefined;

      if (!isPointInRect(mousePosition, cardStackArea)) {
        tracking.trackAnalyticsEvent("live-card-stack-drag-card-out");

        const targetPosition = viewportToStage(stageParams, e.target.getAbsolutePosition());
        const integrationId = tasksIntegrations?.find(
          (t) => t.configuration.boardId === b.boardId && t.configuration.type == MondayIntegrationType.tasks
        )?.id;

        if (integrationId) {
          canvasUtils?.addIntegrationItem(targetPosition, integrationId, IntegrationType.monday, itemId);
        } else {
          if (mondayIntegration) {
            addMondayIntegration(
              b.boardId,
              mondayIntegration.id,
              b.columns,
              MondayIntegrationType.tasks,
              selectedColumns,
              parentBoardId,
              isSubitemsBoard
            )
              .then((integrationId) =>
                canvasUtils?.addIntegrationItem(targetPosition, integrationId, IntegrationType.monday, itemId)
              )
              .catch(console.error);
          } else {
            console.log("cannot drag a card out - get monday integration if possible");
          }
        }
        // hack to trigger react re-render of the element to sort all the cards again after
        // drag-and-drop shuffled them around

        changeElement({ items_order: [...items_order], subitems_order: { ...subitems_order } }, { shouldAdd: false });
      } else {
        const targetYPosition = mousePosition.y - cardLayoutStartY;
        const targetPlace = Math.floor(clamp(targetYPosition / (CARD_HEIGHT + GAP_Y), 0, 9999));

        if (b.boardId === selectedSubBoardId) {
          // Handle subitem reordering - keep subitems under their parent
          const parentItem = allItems.find((item) => item.subitems?.some((sub: any) => sub.id === itemId));
          if (!parentItem) {
            console.log("can't find parent item for subitem");
            return;
          }

          const parentSubitems = parentItem.subitems || [];
          const currentSubitemsOrder = subitems_order[parentItem.id] || parentSubitems.map((sub: any) => sub.id);

          // Get current order of all items and subitems for display
          let displayOrder: string[] = [];
          allItems.forEach((item) => {
            displayOrder.push(item.id);
            if (showSubItems && item.subitems) {
              displayOrder.push(...item.subitems.map((sub: any) => sub.id));
            }
          });

          const originalPlace = displayOrder.indexOf(itemId);
          if (originalPlace === -1) {
            console.log("can't find subitem in display order");
            return;
          }

          // Find the bounds for this parent's subitems
          const parentIndex = displayOrder.indexOf(parentItem.id);
          const firstSubitemIndex = parentIndex + 1;
          const lastSubitemIndex = parentIndex + parentSubitems.length;

          // Constrain target position to within parent's subitem range
          const constrainedTargetPlace = clamp(targetPlace, firstSubitemIndex, lastSubitemIndex);

          if (constrainedTargetPlace !== originalPlace) {
            // Calculate the target index within the subitems array
            const targetSubitemIndex = constrainedTargetPlace - firstSubitemIndex;
            const originalSubitemIndex = currentSubitemsOrder.indexOf(itemId);

            if (originalSubitemIndex !== -1) {
              // Reorder subitems within the parent
              const newSubitemsOrder = [...currentSubitemsOrder];
              newSubitemsOrder.splice(originalSubitemIndex, 1);
              newSubitemsOrder.splice(targetSubitemIndex, 0, itemId);

              // Update the subitems_order for this parent
              const updatedSubitemsOrder = {
                ...subitems_order,
                [parentItem.id]: newSubitemsOrder,
              };

              changeElement({ subitems_order: updatedSubitemsOrder }, { shouldAdd: false });
            }
          }
        } else {
          // Handle main item reordering
          let displayOrder = items_order.length ? items_order : itemIds;
          const originalPlace = displayOrder.indexOf(itemId);
          if (originalPlace === -1) {
            console.log("can't find the target");
            return;
          }

          if (targetPlace != originalPlace) {
            let order = displayOrder.slice();
            order.splice(originalPlace, 1);
            if (targetPlace === 0) {
              order = [itemId, ...order];
            } else {
              order.splice(targetPlace, 0, itemId);
            }
            changeElement({ items_order: order }, { shouldAdd: false });
          }
        }
      }
    } catch (error) {
      console.log("error: ", error);
    }
  }

  function getSubitemsBoardInfo() {
    if (!subItemsIntegrationId || !selectedSubBoardId || !subitemsColumns) {
      return undefined;
    }
    const subBoardIntegrationConfig: any = subItemsIntegrationId
      ? getBoardIntegrationConfig(subItemsIntegrationId)
      : {};
    const columnIdsInSubboard = new Set(
      pluck("id", (subBoardIntegrationConfig?.config?.columns || []) as { id: string }[])
    );

    return {
      integrationId: subItemsIntegrationId,
      boardId: selectedSubBoardId,
      columns: subitemsColumns,
      columnsToDisplay: columnIdsInSubboard,
      name: boardInfo.data.subitemsBoard.name,
    } as const;
  }

  return (
    <>
      <BaseCanvasElement
        id={id}
        type={"liveIntegration"}
        x={element.x}
        y={element.y}
        element={element}
        onResize={onResize}
        mutation={mutation}
        isSelectable={true}
        isEditingLink={false}
        changeElement={changeElement}
      >
        <Rect width={element.width} height={element.height} fill={"transparent"} />
      </BaseCanvasElement>

      <LiveCardStackFrame
        x={element.x}
        y={element.y}
        width={width}
        height={Math.min(heightFromElement, heightForCards)}
        title={element.title}
        isEditing={isEditing}
        onStopEditing={onStopEditing}
        onChangeTitle={function (newValue: string): void {
          changeElement({ title: newValue }, { shouldAdd: false });
        }}
        showSubitems={selectedSubBoardId ? !!element.showSubItems : undefined}
        onShowSubItems={() => {
          tracking.trackAnalyticsEvent("live-card-stack-toggle-subitems", { showSubItems: !element.showSubItems });
          changeElement({ showSubItems: !element.showSubItems }, { shouldAdd: true });
        }}
        name={`cardstack-${id}-frame`}
      />

      {isError && (
        <Group
          x={element.x}
          y={element.y + HEADER + PADDING_Y_TOP * 13}
          width={width}
          height={100}
          name={`cardstack-${id}-error`}
        >
          <Group x={width / 2 - 25} y={-35}>
            <Ellipse x={22} y={19.4229} radiusX={22} radiusY={4.5} fill="#F2F2F2" />
            <Circle x={22.0156} y={10.5} radius={10} fill="#FFD2CF" stroke="#FF6A5F" strokeWidth={1} />
            <Line points={[18.0156, 10.5, 26.0156, 10.5]} stroke="#FF6A5F" strokeWidth={1} lineCap="round" />
          </Group>
          <Text
            x={0}
            y={0}
            width={width}
            height={100}
            text={`Unable to load board`}
            fontFamily="Poppins"
            fontSize={12}
            fill="#1E1E1E"
            align="center"
          />
          {/* <Text
            x={0}
            y={25}
            width={width}
            text={"Retry"}
            fontFamily="Poppins"
            fontSize={12}
            fill="#1973FF"
            align="center"
            cursor="pointer"
            onMouseEnter={(e) => {
              setMousePointer(e);
            }}
            onMouseLeave={(e) => {
              unsetMouse(e);
            }}
            onClick={() => {
              reloadBoardIntegrations();
            }}
          /> */}
        </Group>
      )}

      {!!documentId && !!integrationId && !!selectedBoardId && allItems.length > 0 && (
        <Group x={element.x + PADDING_X} y={element.y + HEADER + PADDING_Y_TOP} name={`cardstack-${id}-cards`}>
          <LiveCardStackContents
            documentId={documentId!}
            board={{
              integrationId: integrationId!,
              boardId: selectedBoardId!,
              columns: boardColumns,
              columnsToDisplay: columnIdsInMainBoard,
              name: boardInfo.data.board.name,
            }}
            subBoard={getSubitemsBoardInfo()}
            allItems={allItems}
            numItemsToDisplay={numCardsToDisplay}
            showSubItems={!!showSubItems}
            calcPosition={(cardIndex: number) => cardPosition(cardIndex, columns)}
            changeElement={changeElement}
            onDragEnd={onDragEnd}
            cardLayoutStartY={cardLayoutStartY}
          />
        </Group>
      )}

      {!isError && (isLoading || isValidating) ? (
        <CardStackFooter
          x={element.x}
          y={element.y + Math.min(heightFromElement, heightForCards) - PADDING_Y_BOTTOM_WITH_MESSAGE}
          width={width}
          height={PADDING_Y_BOTTOM_WITH_MESSAGE + 2}
          text={`loading...`}
          onClick={expandHeightForAllCards}
          name={`cardstack-${id}-footer`}
        />
      ) : numOfUndisplayedCards > 0 ? (
        <CardStackFooter
          x={element.x}
          y={element.y + Math.min(heightFromElement, heightForCards) - PADDING_Y_BOTTOM_WITH_MESSAGE}
          width={width}
          height={PADDING_Y_BOTTOM_WITH_MESSAGE + 2}
          text={`show ${numOfUndisplayedCards} more`}
          onClick={expandHeightForAllCards}
          name={`cardstack-${id}-footer`}
        />
      ) : hasMore ? (
        <CardStackFooter
          x={element.x}
          y={element.y + Math.min(heightFromElement, heightForCards) - PADDING_Y_BOTTOM_WITH_MESSAGE}
          width={width}
          height={PADDING_Y_BOTTOM_WITH_MESSAGE + 2}
          text={`load more`}
          onClick={data.loadMore}
          name={`cardstack-${id}-footer`}
        />
      ) : null}
    </>
  );
}

export function liveIntegrationTraits(element: LiveIntegrationElement): ITraits {
  return {
    liveIntegration: {
      integrationId: element.integrationId,
      filters: element.filters ?? [],
    },
  };
}

export function liveIntegrationValidateTraits(_element: LiveIntegrationElement, _trait: Trait, value: any) {
  return {
    integrationId: value.integrationId,
    filters: value.filters ?? [],
    items_order: [],
  };
}
